import "keen-slider/keen-slider.min.css";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import NewLayout from "@components/new-ui/Layout";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const ApolloJBPHospitals: React.FC = () => {
    return (
        <NewLayout
            title="Apollo JBP Hospitals | Healthcare X WhatsApp Automation"
            description="Discover how Apollo JBP Hospitals boosted lead nurturing, streamlined patient communication, and improved service efficiency with WhatsApp automation."
            canonical="https://gallabox.com/case-study/apollo-jbp-hospitals"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/apollo-jbp-hospitals-og.png" }}
        >
            <CaseStudyHerosection
                title="How Apollo JBP Hospitals transformed patient communication and marketing with Gallabox"
                description="Discover how Apollo JBP Hospitals boosted lead nurturing, streamlined patient communication, and improved service efficiency with WhatsApp automation."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/apollo-jbp-hospitals.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "80%", label: "Improvement in overall business growth" },
                    { percentage: "90%", label: "Growth in customer satisfaction rates" },
                    { percentage: "50%", label: "Reduction in marketing and advertising spend" },
                ]}
            />
            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Apollo JBP Hospitals"] },
                                { label: "Industry", value: ["Healthcare"] },
                            ]}
                            product={{
                                title: "Features used most",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "Flows" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared Team Inbox" },
                                    { link: "https://gallabox.com/click-to-whatsapp-ads", label: "Click-to-WhatsApp Ads" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p className="mt-2">
                                Apollo JBP Hospitals is a leading healthcare provider committed to delivering world-class medical services to patients. With a growing footprint and
                                expanding patient base, the hospital needed a more streamlined approach to marketing, communication, and patient engagement.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"Challenges before Gallabox"}
                                descriptions={["Apollo JBP Hospitals faced several hurdles in integrating WhatsApp into their communication and marketing workflow:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Integration & automation challenges: The hospital wanted to integrate and automate the WhatsApp API with its new marketing and communications workflow. However, service providers they initially approached did not provide convincing or satisfactory solutions.",
                                    },
                                    {
                                        listItem:
                                            "High dropout rates in lead generation: Marketing campaigns primarily ran through Meta lead generation ads, with data captured via Facebook forms. A high percentage of prospective patients dropped out while filling forms, and many avoided sharing essential details such as name and phone number.",
                                    },
                                    {
                                        listItem:
                                            "Manual communication workflows: For communication, the hospital relied on the WhatsApp Business app and telecalling. While functional, this approach was resource-heavy and lacked automation, limiting the hospital's ability to scale patient engagement effectively.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation
                                subTitle={"Initial solutions/tools tried"}
                                descriptions={["Before Gallabox, Apollo JBP Hospitals attempted the following:"]}
                                className="mt-12"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "WhatsApp automation tools like Wati and AiSensy" },
                                    { listItem: "Meta lead generation ads to attract prospective patients, but experienced high form drop-offs." },
                                    { listItem: "WhatsApp Business app for direct communication." },
                                    { listItem: "Telecalling for follow-ups and patient engagement." },
                                    { listItem: "CRMs like ZohoCRM and TeleCRM" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <p className="mt-5">
                                While these platforms offered partial solutions, none matched Gallabox&apos;s ease of onboarding, healthcare experience, and responsiveness. Also,
                                these processes were manual, inefficient, and unable to handle the hospital&apos;s growing demand for real-time engagement.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"Why Apollo JBP hospitals chose Gallabox"}
                                descriptions={[
                                    "Apollo JBP Hospitals partnered with Gallabox because it offered the perfect mix of simplicity, expertise, and reliability. The platform was customer-friendly and easy to adopt, backed by excellent support that ensured smooth implementation and ongoing guidance.",
                                    "Gallabox's deep product knowledge helped the hospital tailor workflows to fit its unique requirements, and its prior experience with other hospitals gave Apollo confidence in the solution.",
                                    "Most importantly, Gallabox's personal touch—responding in person to the very first enquiry—stood out as a differentiator.",
                                    "With Gallabox in place, Apollo JBP Hospitals set out to:",
                                ]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Generate and nurture leads more effectively." },
                                    { listItem: "Streamline patient communication workflows for faster response times." },
                                    { listItem: "Retain existing patients through consistent engagement." },
                                    { listItem: "Increase cross-sell and upsell opportunities across healthcare services." },
                                    { listItem: "Improve overall team productivity with automation." },
                                    { listItem: "Automate patient support and follow-ups to reduce manual workload." },
                                    { listItem: "Close more deals, ensuring sustainable growth." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"Impact on business growth"}
                                descriptions={["Since implementing Gallabox, Apollo JBP Hospitals has seen transformative results:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "80% improvement in overall business growth." },
                                    { listItem: "Employee productivity increased by 100%." },
                                    { listItem: "Customer satisfaction rates rose by 90%." },
                                    { listItem: "Lead generation doubled, with a 100% increase." },
                                    { listItem: "First-time customer conversions grew by 100%." },
                                    { listItem: "Customer base expanded by 100%." },
                                    { listItem: "Marketing and advertising spend reduced by 50%." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation
                                subTitle={"Building healthier patient relationships at scale"}
                                descriptions={[
                                    "With Gallabox, Apollo JBP Hospitals transformed its marketing and communication workflows from manual, fragmented processes into a fully automated and scalable system. The hospital not only achieved higher lead generation and patient conversions but also improved team productivity and reduced costs.",
                                    "Gallabox has become a trusted partner in Apollo JBP Hospitals' journey to deliver exceptional patient experiences while driving sustainable business growth.",
                                ]}
                                className="mt-10"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto">
                <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-20">
                    {casestudyCardsDetails?.map((data, index) => (
                        <CasestudyCard key={index} {...data} />
                    ))}
                </div>
            </section>
        </NewLayout>
    );
};

export default ApolloJBPHospitals;
